/**
 * Browser OIDC — Authorization Code + PKCE (§P7). A public SPA client: no client
 * secret, PKCE (S256) instead. The resulting access token is sent to our API as a
 * bearer; the API validates it against the issuer JWKS exactly as before, so this
 * module only concerns the browser half (redirect → callback → token exchange).
 *
 * Network-free helpers (base64url / PKCE / URL building / claim decode) are pure
 * and exported for unit tests; `beginLogin`/`completeLogin` touch `window`.
 */
export interface OidcConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  /** Optional API audience (Auth0-style); Entra binds via scope instead. */
  audience?: string;
}

const STATE_KEY = 'dgt.oidc.state';
const VERIFIER_KEY = 'dgt.oidc.verifier';

/** Base64url encode (no padding) — the encoding PKCE and JWT segments use. */
export function base64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** A URL-safe random string of `bytes` bytes (32 → 43-char PKCE verifier). */
export function randomString(bytes = 32): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return base64UrlEncode(arr);
}

/** The S256 PKCE code challenge for a verifier: base64url(SHA-256(verifier)). */
export async function codeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

export interface Discovery {
  authorization_endpoint: string;
  token_endpoint: string;
}

let discoveryCache: Promise<Discovery> | null = null;

/** Fetch (and cache) the issuer's OIDC discovery document. */
export async function discover(issuer: string): Promise<Discovery> {
  if (!discoveryCache) {
    const base = issuer.endsWith('/') ? issuer : `${issuer}/`;
    const url = new URL('.well-known/openid-configuration', base).toString();
    discoveryCache = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`OIDC discovery failed (${r.status}).`);
      return r.json() as Promise<Discovery>;
    });
  }
  return discoveryCache;
}

/** Build the /authorize redirect URL (pure — endpoint supplied by the caller). */
export function buildAuthorizeUrl(
  authorizationEndpoint: string,
  cfg: OidcConfig,
  state: string,
  challenge: string,
): string {
  const u = new URL(authorizationEndpoint);
  u.searchParams.set('response_type', 'code');
  u.searchParams.set('client_id', cfg.clientId);
  u.searchParams.set('redirect_uri', cfg.redirectUri);
  u.searchParams.set('scope', cfg.scope);
  u.searchParams.set('state', state);
  u.searchParams.set('code_challenge', challenge);
  u.searchParams.set('code_challenge_method', 'S256');
  if (cfg.audience) u.searchParams.set('audience', cfg.audience);
  return u.toString();
}

/** Step 1: stash PKCE state (CSRF `state` + verifier) and redirect to the IdP. */
export async function beginLogin(cfg: OidcConfig): Promise<void> {
  const state = randomString(16);
  const verifier = randomString(32);
  const challenge = await codeChallenge(verifier);
  window.sessionStorage.setItem(STATE_KEY, state);
  window.sessionStorage.setItem(VERIFIER_KEY, verifier);
  const { authorization_endpoint } = await discover(cfg.issuer);
  window.location.assign(buildAuthorizeUrl(authorization_endpoint, cfg, state, challenge));
}

export interface TokenResponse {
  access_token: string;
  id_token?: string;
  token_type?: string;
  expires_in?: number;
}

/**
 * Step 2 (on the redirect back): verify `state`, then exchange the code for
 * tokens using the stored PKCE verifier. Consumes the stored state either way so
 * a code can't be replayed.
 */
export async function completeLogin(cfg: OidcConfig, params: URLSearchParams): Promise<TokenResponse> {
  const state = params.get('state');
  const code = params.get('code');
  const expectedState = window.sessionStorage.getItem(STATE_KEY);
  const verifier = window.sessionStorage.getItem(VERIFIER_KEY);
  window.sessionStorage.removeItem(STATE_KEY);
  window.sessionStorage.removeItem(VERIFIER_KEY);

  const err = params.get('error');
  if (err) throw new Error(`Sign-in failed: ${params.get('error_description') ?? err}`);
  if (!code || !state) throw new Error('Malformed OIDC callback (missing code/state).');
  if (!expectedState || state !== expectedState) throw new Error('OIDC state mismatch — sign-in aborted.');
  if (!verifier) throw new Error('PKCE verifier missing (session lost). Start sign-in again.');

  const { token_endpoint } = await discover(cfg.issuer);
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: cfg.redirectUri,
    client_id: cfg.clientId,
    code_verifier: verifier,
  });
  const res = await fetch(token_endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) throw new Error(`Token exchange failed (${res.status}).`);
  return (await res.json()) as TokenResponse;
}

/** Best-effort decode of a JWT's claims for display only (NOT verification). */
export function decodeJwtClaims(jwt: string): Record<string, unknown> {
  const part = jwt.split('.')[1];
  if (!part) return {};
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** True when the current query string is an OIDC redirect callback. */
export function isCallback(params: URLSearchParams): boolean {
  return params.has('code') || params.has('error');
}
